import * as React from "react";
import { Button } from "./ui/Button";
import { Icon } from "./ui/Icon";
import { MarkdownBody } from "./scene/MarkdownBody";
import { text } from "../hooks/useContent";
import type { SiteContent } from "../api/content";

const FAQ_COUNT = 36;

/** Spec sheet body for the "Feature overview" modal: the radio.specs.rows
 *  "LABEL :: value" lines as a two-column dl. Light tone — the modal is a
 *  white surface (the in-flow desktop version was white-on-dusk). */
export function SpecsBody({ content }: { content: SiteContent | undefined }) {
  const rows = text(content, "radio.specs.rows")
    .split("\n")
    .map((line) => line.split(" :: "))
    .filter((pair): pair is [string, string] => pair.length === 2);
  return (
    <dl className="m-0 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
      {rows.map(([label, value]) => (
        <div key={label} className="border-b border-primary/10 py-3">
          <dt className="text-tag uppercase tracking-eyebrow text-primary/50">
            {label}
          </dt>
          <dd className="m-0 mt-1 text-[15px] font-medium leading-snug text-primary">
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** One collapsible Q&A row — light-only (the modal surface is always white,
 *  so the previous dark branch is dropped). grid-rows collapse animates
 *  open/close without measuring heights. */
function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div
      className={`rounded-xl border transition-colors ${
        open
          ? "border-primary/15 bg-[color:var(--neutral-100)]"
          : "border-transparent hover:bg-[color:var(--neutral-100)]/60"
      }`}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-5 px-5 py-4 text-left text-primary"
      >
        <span className="text-[17px] font-medium leading-snug">{q}</span>
        <span
          className={`flex h-7 w-7 flex-none items-center justify-center rounded-full border border-primary/20 text-primary transition-transform duration-300 ${
            open ? "rotate-180 bg-primary/5" : ""
          }`}
        >
          <Icon name="chevron-down" />
        </span>
      </button>
      <div
        aria-hidden={!open}
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-5 pb-5">
            <MarkdownBody body={a} surface="light" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** FAQ body for the modal: the radio.faq.q{n}.* accordion plus the order CTA
 *  (radio.faq.cta). `onOrder` closes the FAQ modal and opens checkout. */
export function FaqBody({
  content,
  onOrder,
}: {
  content: SiteContent | undefined;
  onOrder: () => void;
}) {
  const items = Array.from({ length: FAQ_COUNT }, (_, i) => i + 1)
    .map((n) => ({
      q: text(content, `radio.faq.q${n}.q`),
      a: text(content, `radio.faq.q${n}.a`),
    }))
    .filter((item) => item.q && item.a);
  return (
    <>
      <div className="flex flex-col gap-1">
        {items.map((item) => (
          <FaqItem key={item.q} q={item.q} a={item.a} />
        ))}
      </div>
      <div className="mt-2 flex justify-center">
        <Button
          variant="solid"
          tone="muted"
          surface="light"
          as="button"
          type="button"
          onClick={onOrder}
        >
          {text(content, "radio.faq.cta")}
        </Button>
      </div>
    </>
  );
}
